import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getWorkflowDefinition,
  createWorkflowDefinition,
  updateWorkflowDefinition,
  addPhase,
  updatePhase as updatePhaseApi,
  deletePhase as deletePhaseApi,
  addTask,
  updateTask as updateTaskApi,
  deleteTask as deleteTaskApi,
  submitForReview,
  approveWorkflow,
  publishWorkflow,
  archiveWorkflowDefinition,
  restoreWorkflowDefinition,
  permanentDeleteWorkflowDefinition,
  getAuditLog,
  getLinkedTemplates,
  getCaseSubtypes,
  fetchSourceAuthority,
  generateWorkflowSuggestion,
  applyWorkflowSuggestion,
  type SourceAuthorityResult,
  type WorkflowSuggestion,
  type SuggestedPhase,
  type WorkflowDefinitionWithRelations,
  type WorkflowPhase,
  type WorkflowTask,
  type WorkflowTaskForm,
  type WorkflowAuditLogEntry,
  type WorkflowStatus,
  type JurisdictionScope,
  type LinkedTemplate,
  type CaseSubtype,
  STATUS_LABELS,
  STATUS_COLORS,
  formatStatuteCitation,
} from './workflowDefinitionService';
import { WorkflowFormLinker } from './WorkflowFormLinker';
import ConfirmActionModal, { type ConfirmVariant } from './ConfirmActionModal';
import WorkflowSuggestionReviewModal from './WorkflowSuggestionReviewModal';
import { useFirmScope } from '../../../hooks/useFirmScope';

interface Props {
  definitionId: string | null;
  onBack: () => void;
  initialStateCode?: string;
  initialPracticeArea?: string;
}

export const WorkflowDefinitionEditor: React.FC<Props> = ({ definitionId, onBack, initialStateCode, initialPracticeArea }) => {
  // Section 5 — surface firm practiceAreas/licensedStates so authoring UI
  // defaults to firm scope rather than showing all 50 states + every
  // practice area.
  const firmScope = useFirmScope();
  const [definition, setDefinition] = useState<WorkflowDefinitionWithRelations | null>(null);
  const [loading, setLoading] = useState(!!definitionId);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [auditLog, setAuditLog] = useState<WorkflowAuditLogEntry[]>([]);
  const [showAudit, setShowAudit] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set());
  const [formLinkerTaskId, setFormLinkerTaskId] = useState<string | null>(null);
  const [linkedTemplates, setLinkedTemplates] = useState<LinkedTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // Confirm modal state (replaces browser confirm() dialogs)
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    variant: ConfirmVariant;
    onConfirm: () => void;
  } | null>(null);

  // Create form state — pre-populated from props if provided. Section 5:
  // state_code defaults to the firm's first licensed state (and practice_area
  // to the firm's first configured practice area) when props do not supply
  // them, so non-platform users don't start authoring outside firm scope.
  const [createForm, setCreateForm] = useState({
    state_code: initialStateCode || 'CA',
    practice_area: initialPracticeArea || 'family_law',
    case_subtype: '',
    case_subtype_mapping_id: null as string | null,
    case_subtype_source_scope: null as string | null,
    label: '',
    description: '',
    source_authority: '',
    source_authority_url: '',
    jurisdiction_scope: 'state' as JurisdictionScope,
  });

  // After firm settings load, adopt firm defaults if the caller didn't
  // supply initialStateCode / initialPracticeArea. Runs only once when firm
  // scope finishes loading to avoid stomping user edits.
  const appliedFirmDefaultsRef = useRef(false);
  useEffect(() => {
    if (firmScope.loading || appliedFirmDefaultsRef.current) return;
    if (definitionId) { appliedFirmDefaultsRef.current = true; return; } // editing an existing row — keep its values
    appliedFirmDefaultsRef.current = true;
    setCreateForm((prev) => {
      const next = { ...prev };
      if (!initialStateCode && firmScope.licensedStates.length > 0) {
        next.state_code = firmScope.licensedStates[0];
      }
      if (!initialPracticeArea && firmScope.practiceAreas.length > 0) {
        next.practice_area = firmScope.practiceAreas[0];
      }
      return next;
    });
  }, [firmScope.loading, firmScope.licensedStates, firmScope.practiceAreas, initialStateCode, initialPracticeArea, definitionId]);

  // Case subtypes from DB for the dropdown
  const [availableSubtypes, setAvailableSubtypes] = useState<CaseSubtype[]>([]);
  const [loadingSubtypes, setLoadingSubtypes] = useState(false);

  // AI-assisted source authority fetch
  const [fetchingSource, setFetchingSource] = useState(false);
  const [sourceResult, setSourceResult] = useState<SourceAuthorityResult | null>(null);
  const [showAlternatives, setShowAlternatives] = useState(false);

  // AI workflow generation
  const [generatingSuggestion, setGeneratingSuggestion] = useState(false);
  const [suggestion, setSuggestion] = useState<WorkflowSuggestion | null>(null);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);

  const canFetchSource = createForm.state_code && createForm.practice_area && createForm.case_subtype;

  const handleGenerateWorkflow = async () => {
    if (!canFetchSource) return;
    setGeneratingSuggestion(true);
    setError(null);
    try {
      const result = await generateWorkflowSuggestion({
        state_code: createForm.state_code,
        practice_area: createForm.practice_area,
        case_subtype: createForm.case_subtype,
        label: createForm.label,
        description: createForm.description || undefined,
        source_authority: createForm.source_authority || undefined,
        source_authority_url: createForm.source_authority_url || undefined,
        jurisdiction_scope: createForm.jurisdiction_scope || undefined,
      });
      // Auto-populate source authority fields from the suggestion
      setCreateForm((prev) => ({
        ...prev,
        source_authority: result.source_authority || prev.source_authority,
        source_authority_url: result.source_authority_url || prev.source_authority_url,
        description: result.description || prev.description,
      }));
      setSuggestion(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'AI workflow generation failed.');
    } finally {
      setGeneratingSuggestion(false);
    }
  };

  const handleAcceptSuggestion = async (acceptedPhases: SuggestedPhase[]) => {
    setApplyingSuggestion(true);
    setError(null);
    try {
      // If no definition exists yet, create it first
      let defId = definition?.id;
      if (!defId) {
        const created = await createWorkflowDefinition(createForm);
        defId = created.id;
      }
      // Apply the accepted phases
      await applyWorkflowSuggestion(defId, acceptedPhases);
      setSuggestion(null);
      showSuccess(`Workflow generated with ${acceptedPhases.length} phases.`);
      // Load the full definition
      const full = await getWorkflowDefinition(defId);
      setDefinition(full);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to apply workflow suggestion.');
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const handleAIFetch = async () => {
    if (!canFetchSource) return;
    setFetchingSource(true);
    setSourceResult(null);
    setShowAlternatives(false);
    try {
      const result = await fetchSourceAuthority({
        state_code: createForm.state_code,
        practice_area: createForm.practice_area,
        case_subtype: createForm.case_subtype,
        label: createForm.label,
      });
      setSourceResult(result);
      setCreateForm((prev) => ({
        ...prev,
        source_authority: result.source_authority || prev.source_authority,
        source_authority_url: result.source_authority_url || prev.source_authority_url,
        description: result.description || prev.description,
      }));
    } catch {
      setError('AI source fetch failed. You can enter the values manually.');
    } finally {
      setFetchingSource(false);
    }
  };

  const applyAlternative = (alt: { url: string; title: string }) => {
    const sectionMatch = alt.title.replace(/\s*[-–—|]\s*.*$/, '').match(/^(.+?)\s*§/);
    const authority = sectionMatch ? sectionMatch[1].trim() : alt.title.slice(0, 80);
    setCreateForm((prev) => ({
      ...prev,
      source_authority: authority,
      source_authority_url: alt.url,
    }));
    setShowAlternatives(false);
  };

  // Fetch subtypes whenever state_code or practice_area changes
  useEffect(() => {
    if (!createForm.state_code || !createForm.practice_area) {
      setAvailableSubtypes([]);
      return;
    }
    let mounted = true;
    const load = async () => {
      setLoadingSubtypes(true);
      try {
        const subtypes = await getCaseSubtypes(createForm.state_code, createForm.practice_area);
        if (mounted) setAvailableSubtypes(subtypes);
      } catch {
        if (mounted) setAvailableSubtypes([]);
      } finally {
        if (mounted) setLoadingSubtypes(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [createForm.state_code, createForm.practice_area]);

  const loadDefinition = useCallback(async () => {
    if (!definitionId) return;
    let mounted = true;
    try {
      setLoading(true);
      setError(null);
      const def = await getWorkflowDefinition(definitionId);
      if (!mounted) return;
      setDefinition(def);
      // Auto-expand all phases
      setExpandedPhases(new Set(def.phases.map((p) => p.id)));
    } catch (err) {
      if (!mounted) return;
      setError(err instanceof Error ? err.message : 'Failed to load definition');
    } finally {
      if (mounted) setLoading(false);
    }
    return () => { mounted = false; };
  }, [definitionId]);

  useEffect(() => {
    loadDefinition();
  }, [loadDefinition]);

  // Load linked templates for published/validated definitions
  useEffect(() => {
    if (!definition || !definitionId) {
      setLinkedTemplates([]);
      return;
    }
    if (definition.status !== 'published' && definition.status !== 'validated') {
      setLinkedTemplates([]);
      return;
    }
    let mounted = true;
    const loadTemplates = async () => {
      try {
        setLoadingTemplates(true);
        const templates = await getLinkedTemplates(definitionId);
        if (mounted) setLinkedTemplates(templates);
      } catch {
        if (mounted) setLinkedTemplates([]);
      } finally {
        if (mounted) setLoadingTemplates(false);
      }
    };
    loadTemplates();
    return () => { mounted = false; };
  }, [definition?.status, definitionId]);

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 5000);
  };

  const isEditable = !definition || definition.status === 'draft' || definition.status === 'under_review';

  // ─── Create Definition ───
  const handleCreate = async () => {
    try {
      setSaving(true);
      setError(null);
      const created = await createWorkflowDefinition(createForm);
      showSuccess(`Workflow "${created.label}" created successfully.`);
      // Reload as full definition
      const full = await getWorkflowDefinition(created.id);
      setDefinition(full);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create definition');
    } finally {
      setSaving(false);
    }
  };

  // ─── Update Definition Fields ───
  const handleUpdateField = async (field: string, value: string | string[]) => {
    if (!definition) return;
    try {
      setError(null);
      await updateWorkflowDefinition(definition.id, { [field]: value });
      await loadDefinition();
      showSuccess(`Updated ${field.replace(/_/g, ' ')}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  // ─── Phases ───
  const handleAddPhase = async () => {
    if (!definition) return;
    try {
      setError(null);
      const phase = await addPhase(definition.id, { name: 'New Phase' });
      showSuccess(`Phase "${phase.name}" added.`);
      await loadDefinition();
      setExpandedPhases((prev) => new Set([...prev, phase.id]));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add phase');
    }
  };

  const handleUpdatePhase = async (phaseId: string, dto: Partial<WorkflowPhase>) => {
    try {
      setError(null);
      await updatePhaseApi(phaseId, dto as any);
      await loadDefinition();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update phase');
    }
  };

  const handleDeletePhase = (phaseId: string) => {
    setConfirmModal({
      title: 'Delete Phase',
      message: 'This will permanently delete this phase and all its tasks. This action cannot be undone.',
      confirmLabel: 'Delete Phase',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setError(null);
          await deletePhaseApi(phaseId);
          showSuccess('Phase deleted.');
          await loadDefinition();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to delete phase');
        }
      },
    });
  };

  // ─── Tasks ───
  const handleAddTask = async (phaseId: string) => {
    try {
      setError(null);
      const task = await addTask(phaseId, { title: 'New Task' });
      showSuccess(`Task "${task.title}" added.`);
      await loadDefinition();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add task');
    }
  };

  const handleUpdateTask = async (taskId: string, dto: Partial<WorkflowTask>) => {
    try {
      setError(null);
      await updateTaskApi(taskId, dto);
      await loadDefinition();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  };

  const handleDeleteTask = (taskId: string) => {
    setConfirmModal({
      title: 'Delete Task',
      message: 'This will permanently delete this task. This action cannot be undone.',
      confirmLabel: 'Delete Task',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setError(null);
          await deleteTaskApi(taskId);
          showSuccess('Task deleted.');
          await loadDefinition();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to delete task');
        }
      },
    });
  };

  // ─── Lifecycle ───
  const handleLifecycleAction = (action: 'submit' | 'approve' | 'publish') => {
    if (!definition) return;

    const config: Record<string, { title: string; message: string; confirmLabel: string; variant: ConfirmVariant }> = {
      submit: {
        title: 'Submit for Review',
        message: `Submit "${definition.label}" for review? This will change the status from Draft to Under Review. A reviewer will need to approve it before it can be published.`,
        confirmLabel: 'Submit for Review',
        variant: 'warning',
      },
      approve: {
        title: 'Approve Workflow',
        message: `Approve "${definition.label}"? This confirms the workflow definition has been reviewed and is ready for publication.`,
        confirmLabel: 'Approve',
        variant: 'info',
      },
      publish: {
        title: 'Publish Workflow',
        message: `Publish "${definition.label}"? Once published, this workflow definition will be available for use in downstream templates and active matters.`,
        confirmLabel: 'Publish',
        variant: 'success',
      },
    };

    const c = config[action];
    setConfirmModal({
      ...c,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setSaving(true);
          setError(null);
          if (action === 'submit') await submitForReview(definition.id);
          if (action === 'approve') await approveWorkflow(definition.id);
          if (action === 'publish') await publishWorkflow(definition.id);
          showSuccess(`Workflow ${action === 'submit' ? 'submitted for review' : action === 'approve' ? 'approved' : 'published'} successfully.`);
          await loadDefinition();
        } catch (err) {
          setError(err instanceof Error ? err.message : `Failed to ${action}`);
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // ─── Archive ───
  const handleArchive = () => {
    if (!definition) return;
    const hasTemplates = linkedTemplates.length > 0;
    const templateWarning = hasTemplates
      ? ` This definition is linked to ${linkedTemplates.length} downstream template${linkedTemplates.length !== 1 ? 's' : ''} — they will no longer receive updates from this statutory source.`
      : '';
    setConfirmModal({
      title: 'Archive Workflow Definition',
      message: `Archive "${definition.label}"?${templateWarning} Archived definitions are hidden from active views but can still be found under the Archived filter. This action can be reversed by an administrator.`,
      confirmLabel: 'Archive',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setSaving(true);
          setError(null);
          await archiveWorkflowDefinition(definition.id);
          showSuccess('Workflow definition archived.');
          onBack();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to archive workflow definition');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // ─── Restore ───
  const handleRestore = () => {
    if (!definition) return;
    setConfirmModal({
      title: 'Restore Workflow Definition',
      message: `Restore "${definition.label}" from archive? It will be returned to Draft status so you can review and re-publish it.`,
      confirmLabel: 'Restore to Draft',
      variant: 'info',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setSaving(true);
          setError(null);
          await restoreWorkflowDefinition(definition.id);
          showSuccess('Workflow definition restored to Draft.');
          await loadDefinition();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to restore workflow definition');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // ─── Permanent Delete ───
  const handlePermanentDelete = () => {
    if (!definition) return;
    setConfirmModal({
      title: 'Permanently Delete',
      message: `Permanently delete "${definition.label}"? This will remove all phases, tasks, and form links. Audit log entries will be preserved but unlinked. This action cannot be undone.`,
      confirmLabel: 'Delete Forever',
      variant: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          setSaving(true);
          setError(null);
          await permanentDeleteWorkflowDefinition(definition.id);
          showSuccess('Workflow definition permanently deleted.');
          onBack();
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to permanently delete workflow definition');
        } finally {
          setSaving(false);
        }
      },
    });
  };

  // ─── Audit ───
  const handleLoadAudit = async () => {
    if (!definition) return;
    try {
      const logs = await getAuditLog(definition.id);
      setAuditLog(logs);
      setShowAudit(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load audit log');
    }
  };

  const togglePhase = (phaseId: string) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseId)) next.delete(phaseId);
      else next.add(phaseId);
      return next;
    });
  };

  // ─── Loading ───
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-lex-accent border-t-transparent" />
      </div>
    );
  }

  // ─── Create New Form ───
  if (!definitionId && !definition) {
    return (
      <div className="space-y-6">
        <button onClick={onBack} className="text-sm text-lex-accent hover:underline flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
          Back to Browser
        </button>

        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Create New Workflow Definition</h2>

        {error && (
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">{error}</div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">State Code *</label>
            <input type="text" maxLength={2} value={createForm.state_code} onChange={(e) => setCreateForm({ ...createForm, state_code: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Practice Area *</label>
            <input type="text" value={createForm.practice_area} onChange={(e) => setCreateForm({ ...createForm, practice_area: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Case Subtype *</label>
            {loadingSubtypes ? (
              <div className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-400 flex items-center gap-2">
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-lex-accent border-t-transparent" />
                Loading subtypes...
              </div>
            ) : availableSubtypes.length > 0 ? (
              <select
                value={createForm.case_subtype}
                onChange={(e) => {
                  const selected = availableSubtypes.find((s) => s.subtype_key === e.target.value);
                  // Section 5 — capture resolver-backed taxonomy metadata
                  // alongside the canonical key so the backend write path
                  // stores mapping_id + source_scope.
                  setCreateForm({
                    ...createForm,
                    case_subtype: e.target.value,
                    case_subtype_mapping_id: selected?.mapping_id ?? null,
                    case_subtype_source_scope: selected?.source_scope ?? null,
                    label: selected ? `${createForm.state_code} ${selected.label}` : createForm.label,
                  });
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
              >
                <option value="">Select a case subtype...</option>
                {availableSubtypes.map((s) => (
                  <option key={s.canonical_key ?? s.subtype_key} value={s.subtype_key}>
                    {s.label}{s.court_form_ref ? ` (${s.court_form_ref})` : ''}
                  </option>
                ))}
              </select>
            ) : (
              // Section 5: plan forbids normal free-text subtype entry in
              // authoring. When the resolver returns no options, show the
              // setup-guidance empty state rather than letting the user type
              // a canonical subtype from scratch.
              <div className="w-full px-3 py-2 text-sm border border-dashed border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 rounded-lg">
                No subtype options available for this state + practice area yet. An admin must add taxonomy rows before authoring a workflow definition here.
              </div>
            )}
            {availableSubtypes.length > 0 && createForm.case_subtype && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {availableSubtypes.find((s) => s.subtype_key === createForm.case_subtype)?.description || ''}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label *</label>
            <input type="text" value={createForm.label} onChange={(e) => setCreateForm({ ...createForm, label: e.target.value })}
              placeholder="e.g. CA Dissolution of Marriage" className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all ${
                fetchingSource ? 'border-violet-400 dark:border-violet-500 animate-pulse' : 'border-gray-300 dark:border-gray-600'
              }`} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Source Authority</label>
            <input type="text" value={createForm.source_authority} onChange={(e) => setCreateForm({ ...createForm, source_authority: e.target.value })}
              placeholder="e.g. California Family Code"
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all ${
                fetchingSource ? 'border-violet-400 dark:border-violet-500 animate-pulse' : 'border-gray-300 dark:border-gray-600'
              }`} />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Source Authority URL</label>
              <button
                type="button"
                onClick={handleAIFetch}
                disabled={!canFetchSource || fetchingSource}
                title={!canFetchSource ? 'Select state, practice area, and subtype first' : 'Search legal sources with AI'}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                  !canFetchSource || fetchingSource
                    ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                    : 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/30 hover:bg-violet-100 dark:hover:bg-violet-900/50 border border-violet-200 dark:border-violet-700 shadow-sm hover:shadow'
                }`}
              >
                {fetchingSource ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-2 border-violet-500 border-t-transparent" />
                    Searching...
                  </>
                ) : (
                  <>
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                    AI Fetch
                  </>
                )}
              </button>
            </div>
            <input type="url" value={createForm.source_authority_url} onChange={(e) => setCreateForm({ ...createForm, source_authority_url: e.target.value })}
              placeholder={fetchingSource ? 'Searching legal sources...' : ''}
              className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white transition-all ${
                fetchingSource ? 'border-violet-400 dark:border-violet-500 animate-pulse' : 'border-gray-300 dark:border-gray-600'
              }`} />

            {/* Confidence badge + alternatives */}
            {sourceResult && !fetchingSource && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  sourceResult.confidence >= 0.8
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                    : sourceResult.confidence >= 0.5
                      ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    sourceResult.confidence >= 0.8 ? 'bg-emerald-500' : sourceResult.confidence >= 0.5 ? 'bg-amber-500' : 'bg-gray-400'
                  }`} />
                  {Math.round(sourceResult.confidence * 100)}% confidence
                </span>
                {sourceResult.alternatives.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowAlternatives(!showAlternatives)}
                    className="text-[10px] text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    {showAlternatives ? 'Hide' : `${sourceResult.alternatives.length} alternative${sourceResult.alternatives.length !== 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            )}

            {/* Alternatives dropdown */}
            {showAlternatives && sourceResult && sourceResult.alternatives.length > 0 && (
              <div className="mt-2 rounded-lg border border-violet-200 dark:border-violet-700 bg-violet-50/50 dark:bg-violet-900/10 overflow-hidden">
                {sourceResult.alternatives.map((alt, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => applyAlternative(alt)}
                    className="w-full text-left px-3 py-2 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors border-b last:border-b-0 border-violet-100 dark:border-violet-800"
                  >
                    <p className="text-xs font-medium text-gray-800 dark:text-gray-200 truncate">{alt.title}</p>
                    <p className="text-[10px] text-violet-600 dark:text-violet-400 truncate">{alt.url}</p>
                    {alt.snippet && <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 line-clamp-1">{alt.snippet}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleCreate}
            disabled={saving || !createForm.state_code || !createForm.practice_area || !createForm.case_subtype || !createForm.label}
            className="px-5 py-2.5 text-sm font-medium text-lex-accent-contrast bg-lex-accent hover:bg-lex-accent-hover disabled:bg-lex-accent/60 rounded-lg shadow-sm transition-colors"
          >
            {saving ? 'Creating...' : 'Create Workflow Definition'}
          </button>
          <button
            type="button"
            onClick={handleGenerateWorkflow}
            disabled={!canFetchSource || generatingSuggestion || !createForm.label}
            title={!canFetchSource ? 'Fill in state, practice area, subtype, and label first' : 'Generate a complete workflow with AI'}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
              !canFetchSource || generatingSuggestion || !createForm.label
                ? 'text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 cursor-not-allowed'
                : 'text-white bg-violet-600 hover:bg-violet-700 shadow-sm hover:shadow'
            }`}
          >
            {generatingSuggestion ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Generating Workflow...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                </svg>
                AI Generate Workflow
              </>
            )}
          </button>
        </div>

        {/* AI Suggestion Review Modal */}
        {suggestion && (
          <WorkflowSuggestionReviewModal
            suggestion={suggestion}
            loading={applyingSuggestion}
            onAccept={handleAcceptSuggestion}
            onCancel={() => setSuggestion(null)}
          />
        )}
      </div>
    );
  }

  if (!definition) return null;
  const statusColor = STATUS_COLORS[definition.status] || STATUS_COLORS.draft;

  // ─── Editor ───
  return (
    <div className="space-y-6">
      {/* Nav */}
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-sm text-lex-accent hover:underline flex items-center gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
          Back to Browser
        </button>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${statusColor.dot}`} />
            {STATUS_LABELS[definition.status]}
          </span>
          <span className="text-xs text-gray-400">v{definition.version}</span>
        </div>
      </div>

      {/* Alerts */}
      {error && <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 rounded-lg p-3 text-sm">{error}</div>}
      {success && <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 rounded-lg p-3 text-sm">{success}</div>}

      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{definition.label}</h2>
        <div className="flex flex-wrap gap-2 mb-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-lex-accent-soft text-lex-accent">{definition.state_code}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">{definition.practice_area.replace(/_/g, ' ')}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">{definition.case_subtype.replace(/_/g, ' ')}</span>
          <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">{definition.jurisdiction_scope}</span>
        </div>
        {definition.description && <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{definition.description}</p>}
        {definition.source_authority && (
          <p className="text-xs text-gray-500 dark:text-gray-500">
            Source: {definition.source_authority_url ? (
              <a href={definition.source_authority_url} target="_blank" rel="noopener noreferrer" className="text-lex-accent hover:underline">
                {definition.source_authority}
              </a>
            ) : definition.source_authority}
          </p>
        )}

        {/* Lifecycle actions */}
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
          {definition.status === 'draft' && (
            <button onClick={() => handleLifecycleAction('submit')} disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 dark:text-amber-300 dark:bg-amber-900/20 dark:hover:bg-amber-900/40 rounded-lg border border-amber-200 dark:border-amber-800 transition-colors">
              Submit for Review
            </button>
          )}
          {definition.status === 'under_review' && (
            <button onClick={() => handleLifecycleAction('approve')} disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-lex-accent bg-lex-accent-soft hover:bg-lex-accent-soft/80 rounded-lg border border-lex-accent/30 transition-colors">
              Approve
            </button>
          )}
          {definition.status === 'validated' && (
            <button onClick={() => handleLifecycleAction('publish')} disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-300 dark:bg-emerald-900/20 dark:hover:bg-emerald-900/40 rounded-lg border border-emerald-200 dark:border-emerald-800 transition-colors">
              Publish
            </button>
          )}
          <button onClick={handleLoadAudit}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
            Audit Log
          </button>
          {definition.status !== 'archived' && (
            <button onClick={handleArchive} disabled={saving}
              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 hover:bg-red-100 dark:text-red-300 dark:bg-red-900/20 dark:hover:bg-red-900/40 rounded-lg border border-red-200 dark:border-red-800 transition-colors ml-auto">
              Archive
            </button>
          )}
          {definition.status === 'archived' && (
            <>
              <button onClick={handleRestore} disabled={saving}
                className="px-3 py-1.5 text-xs font-medium text-lex-accent bg-lex-accent-soft hover:bg-lex-accent-soft/80 rounded-lg border border-lex-accent/30 transition-colors ml-auto">
                Restore
              </button>
              <button onClick={handlePermanentDelete} disabled={saving}
                className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 rounded-lg transition-colors">
                Permanently Delete
              </button>
            </>
          )}
        </div>
      </div>

      {/* ─── Linked Templates (Pipeline Visibility) ─── */}
      {definition.status === 'published' || definition.status === 'validated' ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-lex-accent-soft to-lex-accent-soft border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-lex-accent" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                </svg>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Downstream Templates
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Templates synthesized from this statutory definition
                </span>
              </div>
              {linkedTemplates.length > 0 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-lex-accent-soft text-lex-accent">
                  {linkedTemplates.length} template{linkedTemplates.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div className="p-4">
            {loadingTemplates ? (
              <div className="flex items-center gap-2 py-3">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-lex-accent border-t-transparent" />
                <span className="text-sm text-gray-500 dark:text-gray-400">Loading linked templates...</span>
              </div>
            ) : linkedTemplates.length === 0 ? (
              <div className="text-center py-6">
                <svg className="mx-auto h-10 w-10 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.875-3.375l4.5-4.5a4.5 4.5 0 00-6.364-6.364L8.257 2.879" />
                </svg>
                <p className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400">
                  No templates synthesized yet
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
                  Go to the <span className="font-medium text-lex-accent">Templates</span> tab and run court intelligence synthesis for a jurisdiction to create templates from this definition.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {linkedTemplates.map((tmpl) => (
                  <div
                    key={tmpl.id}
                    className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-lex-accent" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {tmpl.name}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {tmpl.jurisdiction_code || 'No jurisdiction'}
                          </span>
                          {tmpl.case_subtype && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300">
                              {tmpl.case_subtype.replace(/_/g, ' ')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        tmpl.lifecycle_status === 'validated'
                          ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                          : tmpl.lifecycle_status === 'stale'
                            ? 'bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300'
                            : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300'
                      }`}>
                        <span className={`w-1 h-1 rounded-full ${
                          tmpl.lifecycle_status === 'validated' ? 'bg-emerald-500'
                            : tmpl.lifecycle_status === 'stale' ? 'bg-rose-500'
                              : 'bg-gray-400'
                        }`} />
                        {tmpl.lifecycle_status}
                      </span>
                      <span className="text-[10px] text-gray-400 dark:text-gray-500">
                        {tmpl.template_kind.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Phases */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Phases ({definition.phases.length})
          </h3>
          {isEditable && (
            <button onClick={handleAddPhase} className="text-xs text-lex-accent hover:underline">
              + Add Phase
            </button>
          )}
        </div>

        {definition.phases
          .sort((a, b) => a.phase_order - b.phase_order)
          .map((phase) => (
          <PhaseAccordion
            key={phase.id}
            phase={phase}
            expanded={expandedPhases.has(phase.id)}
            onToggle={() => togglePhase(phase.id)}
            isEditable={isEditable}
            onUpdatePhase={handleUpdatePhase}
            onDeletePhase={handleDeletePhase}
            onAddTask={handleAddTask}
            onUpdateTask={handleUpdateTask}
            onDeleteTask={handleDeleteTask}
            onLinkForm={(taskId) => setFormLinkerTaskId(taskId)}
          />
        ))}
      </div>

      {/* Audit Log Modal */}
      {showAudit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Audit Log</h3>
              <button onClick={() => setShowAudit(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            {auditLog.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No audit entries found.</p>
            ) : (
              <div className="space-y-3">
                {auditLog.map((entry) => (
                  <div key={entry.id} className="border-l-2 border-gray-200 dark:border-gray-600 pl-3 py-1">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{entry.action.replace(/_/g, ' ')}</span>
                      <span>{entry.entity_type}</span>
                      {entry.actor_name && <span>by {entry.actor_name}</span>}
                      <span>{new Date(entry.created_at).toLocaleString()}</span>
                    </div>
                    {entry.notes && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{entry.notes}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form Linker Modal */}
      {formLinkerTaskId && (
        <WorkflowFormLinker
          taskId={formLinkerTaskId}
          onClose={() => setFormLinkerTaskId(null)}
          onLinked={() => { setFormLinkerTaskId(null); loadDefinition(); }}
        />
      )}

      {/* Styled Confirm Modal (replaces browser confirm dialogs) */}
      {confirmModal && (
        <ConfirmActionModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          variant={confirmModal.variant}
          loading={saving}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(null)}
        />
      )}
    </div>
  );
};

// ─── Phase Accordion Sub-Component ───

interface PhaseAccordionProps {
  phase: WorkflowPhase & { tasks: (WorkflowTask & { forms?: WorkflowTaskForm[] })[] };
  expanded: boolean;
  onToggle: () => void;
  isEditable: boolean;
  onUpdatePhase: (phaseId: string, dto: Partial<WorkflowPhase>) => void;
  onDeletePhase: (phaseId: string) => void;
  onAddTask: (phaseId: string) => void;
  onUpdateTask: (taskId: string, dto: Partial<WorkflowTask>) => void;
  onDeleteTask: (taskId: string) => void;
  onLinkForm: (taskId: string) => void;
}

const PhaseAccordion: React.FC<PhaseAccordionProps> = ({
  phase, expanded, onToggle, isEditable,
  onUpdatePhase, onDeletePhase, onAddTask, onUpdateTask, onDeleteTask, onLinkForm,
}) => {
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(phase.name);

  const saveName = () => {
    if (nameInput.trim() && nameInput !== phase.name) {
      onUpdatePhase(phase.id, { name: nameInput.trim() } as any);
    }
    setEditingName(false);
  };

  const tasks = (phase.tasks || []).sort((a, b) => a.task_order - b.task_order);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      {/* Phase header */}
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
        </svg>
        <div className="flex-1 min-w-0">
          {editingName ? (
            <input
              autoFocus
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
              onClick={(e) => e.stopPropagation()}
              className="text-sm font-semibold text-gray-900 dark:text-white bg-transparent border-b border-lex-accent focus:outline-none"
            />
          ) : (
            <span
              className="text-sm font-semibold text-gray-900 dark:text-white"
              onDoubleClick={(e) => { if (isEditable) { e.stopPropagation(); setEditingName(true); } }}
            >
              {phase.phase_order}. {phase.name}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
          {phase.is_court_mandated && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-[10px] font-medium">
              Court Mandated
            </span>
          )}
          <span>{tasks.length} task{tasks.length !== 1 ? 's' : ''}</span>
          {phase.estimated_duration_days != null && <span>{phase.estimated_duration_days}d est.</span>}
        </div>
      </button>

      {/* Phase content */}
      {expanded && (
        <div className="border-t border-gray-100 dark:border-gray-700">
          {phase.mandate_citation && (
            <div className="px-4 py-2 bg-amber-50/50 dark:bg-amber-900/10 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
              </svg>
              {formatStatuteCitation(phase.mandate_citation)}
            </div>
          )}

          {/* Tasks */}
          <div className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                isEditable={isEditable}
                onUpdate={onUpdateTask}
                onDelete={onDeleteTask}
                onLinkForm={onLinkForm}
              />
            ))}
          </div>

          {/* Add task button */}
          {isEditable && (
            <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <button onClick={() => onAddTask(phase.id)} className="text-xs text-lex-accent hover:underline">
                  + Add Task
                </button>
                <span className="text-gray-300">|</span>
                <button onClick={() => onDeletePhase(phase.id)} className="text-xs text-rose-500 dark:text-rose-400 hover:underline">
                  Delete Phase
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Task Row Sub-Component ───

interface TaskRowProps {
  task: WorkflowTask & { forms?: WorkflowTaskForm[] };
  isEditable: boolean;
  onUpdate: (taskId: string, dto: Partial<WorkflowTask>) => void;
  onDelete: (taskId: string) => void;
  onLinkForm: (taskId: string) => void;
}

const PRIORITY_COLORS = {
  low: 'text-gray-500',
  medium: 'text-lex-accent dark:text-lex-accent',
  high: 'text-amber-600 dark:text-amber-400',
  critical: 'text-rose-600 dark:text-rose-400',
};

const TaskRow: React.FC<TaskRowProps> = ({ task, isEditable, onUpdate, onDelete, onLinkForm }) => {
  const [expanded, setExpanded] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleInput, setTitleInput] = useState(task.title);

  const saveTitle = () => {
    if (titleInput.trim() && titleInput !== task.title) {
      onUpdate(task.id, { title: titleInput.trim() } as any);
    }
    setEditingTitle(false);
  };

  return (
    <div className="px-4 py-2.5">
      {/* Task header line */}
      <div className="flex items-start gap-2">
        <button onClick={() => setExpanded(!expanded)} className="mt-0.5">
          <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
          </svg>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${PRIORITY_COLORS[task.priority] || PRIORITY_COLORS.medium}`}>
              {task.priority.charAt(0).toUpperCase()}
            </span>
            {editingTitle ? (
              <input
                autoFocus
                value={titleInput}
                onChange={(e) => setTitleInput(e.target.value)}
                onBlur={saveTitle}
                onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') setEditingTitle(false); }}
                className="flex-1 text-sm text-gray-900 dark:text-white bg-transparent border-b border-lex-accent focus:outline-none"
              />
            ) : (
              <span
                className="text-sm text-gray-800 dark:text-gray-200"
                onDoubleClick={() => { if (isEditable) setEditingTitle(true); }}
              >
                {task.title}
              </span>
            )}
          </div>

          {/* Statute citation - prominent */}
          {task.statute_citation && (
            <div className="flex items-center gap-1.5 mt-1">
              <svg className="w-3 h-3 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" />
              </svg>
              <span className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                {task.statute_url ? (
                  <a href={task.statute_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    {formatStatuteCitation(task.statute_citation)}
                  </a>
                ) : formatStatuteCitation(task.statute_citation)}
              </span>
              {task.rule_citation && (
                <span className="text-xs text-gray-400 dark:text-gray-500">| {task.rule_citation}</span>
              )}
            </div>
          )}
        </div>

        {/* Right side badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {task.is_court_mandated && (
            <span className="px-1.5 py-0.5 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 text-[10px] font-medium">
              Mandated
            </span>
          )}
          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px]">
            +{task.due_date_offset_days}d
          </span>
          <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-[10px]">
            {task.task_type}
          </span>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="ml-5.5 mt-2 space-y-2 pl-4 border-l-2 border-gray-100 dark:border-gray-700">
          {task.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{task.description}</p>
          )}
          {task.notes && (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">{task.notes}</p>
          )}

          {/* Forms */}
          {task.forms && task.forms.length > 0 && (
            <div>
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Required Forms:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {task.forms.map((f) => (
                  <span key={f.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-lex-accent-soft text-lex-accent">
                    {f.form_number}
                    {f.is_mandatory && <span className="text-rose-500">*</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Task metadata */}
          <div className="flex flex-wrap gap-3 text-xs text-gray-400 dark:text-gray-500">
            {task.assignee_role && <span>Role: {task.assignee_role}</span>}
            {task.estimated_hours != null && <span>{task.estimated_hours}h est.</span>}
            {task.creates_calendar_event && <span>Calendar event</span>}
            {task.service_method && <span>Service: {task.service_method}</span>}
            {task.service_proof_required && <span>Proof req.</span>}
          </div>

          {/* Actions */}
          {isEditable && (
            <div className="flex gap-2 pt-1">
              <button onClick={() => onLinkForm(task.id)} className="text-xs text-lex-accent hover:underline">
                Link Form
              </button>
              <button onClick={() => onDelete(task.id)} className="text-xs text-rose-500 dark:text-rose-400 hover:underline">
                Delete
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
